import { GetBankInstitutionNameErrorType, useAchInstitutionName, useWireInstitutionName } from '@egw/api'
import { type FC, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from '@egw/i18n'
import { ROUTING_NUMBER_MASK } from '@egw/constants'
import { Input } from '@egw/components'
import { RoutingNumberType } from '../../../types'
import { BankNameVerified } from '../bank-name-verified/bank-name-verified'

export const RoutingNumberInput: FC<{
  id: string
  autoFocus?: boolean
  routingNumberType: RoutingNumberType
  displayLabel: string
}> = ({ id, autoFocus, routingNumberType, displayLabel }) => {
  const { t } = useTranslation('features-transfer')

  const { watch, setError, formState, control } = useFormContext()

  const routingNumberValue = watch(id) as string

  const hasErrors = formState.errors[id]

  const {
    data: achBankNameResponse,
    isFetching: achBankFetching,
    isFetched: achBankFetched,
  } = useAchInstitutionName(routingNumberValue, {
    enabled: Boolean(
      routingNumberValue.length === ROUTING_NUMBER_MASK.length &&
        routingNumberType === RoutingNumberType.ACH_INSTITUTION,
    ),
  })
  const achBankName = useMemo(() => {
    if (achBankNameResponse?.__typename === 'BankingInstitution') {
      return achBankNameResponse.bankName
    }
    return achBankNameResponse?.type
  }, [achBankNameResponse])

  const {
    data: wireBankNameResponse,
    isFetching: wireBankFetching,
    isFetched: wireBankFetched,
  } = useWireInstitutionName(routingNumberValue, {
    enabled: Boolean(
      routingNumberValue.length === ROUTING_NUMBER_MASK.length &&
        routingNumberType === RoutingNumberType.WIRE_INSTITUTION,
    ),
  })
  const wireBankName = useMemo(() => {
    if (wireBankNameResponse?.__typename === 'BankingInstitution') {
      return wireBankNameResponse.bankName
    }
    return wireBankNameResponse?.type
  }, [wireBankNameResponse])

  const { isFetching, isFetched, bankName } = useMemo(
    () =>
      ({
        [RoutingNumberType.ACH_INSTITUTION]: {
          isFetching: achBankFetching,
          isFetched: achBankFetched,
          bankName: achBankName,
        },
        [RoutingNumberType.WIRE_INSTITUTION]: {
          isFetching: wireBankFetching,
          isFetched: wireBankFetched,
          bankName: wireBankName,
        },
      })[routingNumberType],
    [routingNumberType, achBankFetched, achBankFetching, achBankName, wireBankFetching, wireBankFetched, wireBankName],
  )

  useEffect(() => {
    //this ensures that the bank name error appear right after the call fails
    if (!hasErrors && bankName === GetBankInstitutionNameErrorType.UNABLE_TO_FIND_INSTITUTION) {
      setError(id, {
        type: 'manual',
        message: t('external_accounts.bank_verification_failure'),
      })
    }
  }, [isFetched, hasErrors, bankName, setError, id, t, routingNumberType])

  return (
    <div>
      <Controller
        name={id}
        control={control}
        rules={{
          required: t('common.required_field'),
        }}
        render={({ field }) => (
          <>
            <Input
              {...field}
              id={id}
              label={displayLabel}
              valueFormatter={(v) => v.replace(/[^0-9]/g, '')}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              value={field.value as string}
              error={hasErrors?.message as string}
              isPII
            />
            {bankName !== GetBankInstitutionNameErrorType.UNABLE_TO_FIND_INSTITUTION && (
              <BankNameVerified isFetching={isFetching} bankName={bankName} />
            )}
          </>
        )}
      />
    </div>
  )
}
